/**
 * The legal surface as cache entries: one published text, and what the caller still owes.
 *
 * **Nothing here is live, and that is a decision rather than an omission** (`~/.claude/CLAUDE.md`,
 * data fetching). A published legal version changes when a deploy publishes one — it cannot
 * change between two requests of one visit, and it never changes because of something the
 * person did on a screen. So there is no subscription, no polling interval, and no
 * invalidation on a broad event; the only writer is the acceptance mutation below, which
 * invalidates the one key its own success falsified.
 *
 * **The document key names no workspace and the outstanding key does**, which is the split
 * `KLQ-20260819-007` §6 asks for: `GET /legal/{kind}` serves bytes identical to every caller in
 * every tenant, while `GET /legal-acceptances/outstanding` answers for the person *and* for the
 * company the request acts as — `WORKSPACE_KINDS` (the AVV) is owed per workspace. So the
 * outstanding key carries the active workspace id, the way `features/studio` and
 * `features/settings` carry it: the header and the key move together, or a studio switch would
 * show one company's obligation under another's chrome.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { useActiveWorkspaceId } from '@/features/session';

import type { Acceptance } from './api';
import {
  acceptLegalDocument,
  listLegalDocuments,
  readLegalDocument,
  readOutstandingAcceptances,
} from './api';

/** One published text, keyed by the kind and the language it was asked for. */
export const LEGAL_DOCUMENT_QUERY_KEY = ['legal'] as const;

export function legalDocumentQueryKey(kind: string, locale: string) {
  return [...LEGAL_DOCUMENT_QUERY_KEY, kind, locale] as const;
}

/**
 * The document itself — read by `/legal/$kind` and by the re-acceptance notice, from one entry.
 *
 * The notice needs the text's `content_hash` and the page needs its body, and both come out of
 * the same response. Two keys for that would mean the notice could send a hash the page never
 * showed, which is precisely the claim the hash exists to make impossible.
 *
 * A `404` is an answer — counsel has not delivered that kind — so it is not retried; the two
 * retries left over are for a network that dropped.
 */
export function legalDocumentQueryOptions(kind: string, locale: string) {
  return queryOptions({
    queryKey: legalDocumentQueryKey(kind, locale),
    queryFn: () => readLegalDocument(kind, locale),
    // A published legal text changes when a new version is deployed, never between two
    // requests of one visit. Refetching it is traffic spent to learn nothing.
    staleTime: 60 * 60_000,
    retry: (failureCount: number, error: Error) =>
      !(error instanceof ApiError && error.status === 404) && failureCount < 2,
  });
}

/**
 * What is published — the index `/legal/$kind` draws its list of documents from.
 *
 * One request for every document page of a visit: the key names no kind and no language,
 * because the index answers the same kinds whichever one is open. A kind with no published
 * version is absent from it, so the page links nothing that would answer "not published".
 */
export function legalIndexQueryOptions() {
  return queryOptions({
    queryKey: [...LEGAL_DOCUMENT_QUERY_KEY, 'index'] as const,
    queryFn: listLegalDocuments,
    staleTime: 60 * 60_000,
  });
}

/**
 * What the caller still owes acceptance on — **fetched once per session and shared**.
 *
 * `staleTime: Infinity` is the mechanism, not a tuning: a query that is never stale is never
 * refetched on a mount, a focus or a reconnect, so the notice mounted above every signed-in
 * screen spends exactly one request for the whole visit. The three `refetchOn…: false` lines
 * are redundant while that holds and are written anyway, the way `normCatalogueQueryOptions`
 * writes them — a `staleTime` shortened for an unrelated reason would otherwise turn chrome
 * into a per-navigation fetch silently.
 *
 * A version published mid-session reaches an open tab on its next load, which is the same
 * moment the rest of that deploy does.
 *
 * **Once per workspace, not once per tab.** The endpoint takes no id — it answers for whichever
 * company the request acted as — so a key without the id would hand studio B the list computed
 * for studio A on every switch, and the AVV is exactly the kind that differs between them.
 * Somebody who acts as two companies in one visit spends two requests, which is still no
 * subscription, no poll and no invalidation on a broad event.
 */
export const OUTSTANDING_ACCEPTANCES_QUERY_KEY = ['legal-acceptances', 'outstanding'] as const;

export function outstandingAcceptancesQueryKey(workspaceId: string) {
  return [...OUTSTANDING_ACCEPTANCES_QUERY_KEY, workspaceId] as const;
}

export function outstandingAcceptancesQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: outstandingAcceptancesQueryKey(workspaceId),
    queryFn: readOutstandingAcceptances,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * The list, for the notice.
 *
 * `enabled` because the notice is drawn inside the chrome, which an unclaimed session does not
 * get: asking for it there would spend a request on a screen that cannot render the answer. It
 * waits for the active workspace for the same reason — a request sent before the session lands
 * would be answered for the server's default company and cached under nobody's id.
 */
export function useOutstandingAcceptances(enabled = true) {
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    ...outstandingAcceptancesQueryOptions(workspaceId ?? ''),
    enabled: enabled && workspaceId !== null,
  });
}

export interface OfferedAcceptance {
  readonly kind: string;
  /** The language the text was **served** in, which is not always the one that was asked for. */
  readonly locale: string;
  readonly contentHash: string;
}

/**
 * Accept one document, then forget the list that said it was owed.
 *
 * `invalidateQueries` on the outstanding key alone. Not `setQueryData` filtering the accepted
 * kind out of the cached array: the server decides what is still owed, and a client that
 * removed the row itself would be asserting an outcome it did not compute — an acceptance that
 * wrote nothing (an unpublished kind answers with no row) would leave the notice gone and the
 * obligation standing. And not a wider invalidation either: nothing else in the cache is
 * falsified by an acceptance, and re-fetching a project list because somebody agreed to a
 * privacy policy is the "refetches on a broad event" failure the ecosystem rule names.
 *
 * The prefix rather than the acting workspace's own entry, because a `PERSONAL_KINDS`
 * acceptance is the person's and is therefore owed by none of their companies afterwards: an
 * entry held for a studio this tab is not currently acting as is falsified by this success too.
 * The width is one endpoint's cached answers, which is what the mutation actually invalidated.
 */
export function useAcceptLegalDocument() {
  const queryClient = useQueryClient();
  return useMutation<Acceptance, Error, OfferedAcceptance>({
    mutationFn: acceptLegalDocument,
    // A `409` is an answer — the text was republished under the open tab — and re-sending the
    // same stale hash earns the same refusal.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OUTSTANDING_ACCEPTANCES_QUERY_KEY });
    },
  });
}
